import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useI18n } from '../../i18n';

interface RevenueByDevice {
  deviceId: number;
  deviceName: string;
  totalRevenue: number;
  transactionCount?: number;
}

interface RevenueByDeviceChartProps {
  data: RevenueByDevice[];
  loading?: boolean;
}

function RevenueByDeviceChart({ data, loading }: RevenueByDeviceChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Check if we have revenue data or only transaction counts
  const hasRevenueData = data.some(d => d.totalRevenue > 0);

  // Sort by revenue or transactions descending and take top 10
  const sortedData = [...data].sort((a, b) => {
    if (hasRevenueData) return b.totalRevenue - a.totalRevenue;
    return (b.transactionCount || 0) - (a.transactionCount || 0);
  }).slice(0, 10);
  
  const categories = sortedData.map(d => d.deviceName);
  const chartData = sortedData.map((d, index) => {
    // Color gradient from dark to light
    const colors = [
      '#065f46', '#047857', '#059669', '#10b981', '#34d399',
      '#6ee7b7', '#a7f3d0', '#d1fae5', '#ecfdf5', '#f0fdf4'
    ];
    return {
      y: hasRevenueData ? d.totalRevenue : (d.transactionCount || 0),
      color: colors[index] || '#10b981',
    };
  });

  const chartTitle = hasRevenueData ? t('leg2_revenue_per_charger_eur') : t('leg2_transactions_per_charger');
  const chartSubtitle = hasRevenueData ? t('leg2_top10_most_profitable') : t('leg2_top10_most_active');
  const yAxisTitle = hasRevenueData ? t('leg2_revenue_eur') : t('leg2_transactions');
  const tooltipFormat = hasRevenueData 
    ? `${t('leg2_revenue')}: <b>{point.y:.2f} €</b>`
    : `${t('leg2_transactions')}: <b>{point.y}</b>`;
  const dataLabelFormat = hasRevenueData ? '{point.y:.0f} €' : '{point.y}';
  const yAxisFormat = hasRevenueData ? '{value} €' : '{value}';

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: 350,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: chartTitle,
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: chartSubtitle,
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          fontSize: '12px',
          color: '#374151',
        },
      },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisTitle,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: yAxisFormat,
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      headerFormat: '<b>{point.key}</b><br/>',
      pointFormat: tooltipFormat,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#10b981',
      borderRadius: 8,
      shadow: true,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: dataLabelFormat,
          style: {
            fontSize: '10px',
            color: '#374151',
            textOutline: 'none',
          },
        },
      },
    },
    series: [{
      type: 'bar',
      name: hasRevenueData ? t('leg2_revenue') : t('leg2_transactions'),
      data: chartData,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
      {data.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-4">
          {t('leg2_no_data_available')}
        </div>
      )}
    </div>
  );
}

export default RevenueByDeviceChart;
